import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HelpCircle, X, PlayCircle, Search, MessageCircle, Sparkles, Lightbulb } from 'lucide-react';
import FabTooltip from '@/components/FabTooltip';

/**
 * HelpButton — persistent, floating help menu in the bottom-right of every page.
 * Sits above BottomNav (mobile) and stays out of the way on desktop.
 */
export default function HelpButton() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const nav = useNavigate();

  useEffect(() => { setOpen(false); }, [pathname]);

  const items = [
    {
      label: 'Take the guided tour', icon: PlayCircle, testid: 'help-menu-tour',
      onClick: () => { window.dispatchEvent(new CustomEvent('bp:replay-tour')); setOpen(false); },
    },
    {
      label: 'Search help', icon: Search, testid: 'help-menu-search',
      onClick: () => { nav('/app/help'); setOpen(false); },
    },
    {
      label: 'Frequently asked questions', icon: HelpCircle, testid: 'help-menu-faq',
      onClick: () => { nav('/app/help#faq'); setOpen(false); },
    },
    {
      label: 'Tips for this page', icon: Lightbulb, testid: 'help-menu-tips',
      onClick: () => {
        try {
          Object.keys(sessionStorage).forEach(k => { if (k.startsWith('bp:tip-hidden:')) sessionStorage.removeItem(k); });
        } catch { /* no-op */ }
        window.dispatchEvent(new CustomEvent('bp:refresh-tips'));
        setOpen(false);
      },
    },
    {
      label: "What's new", icon: Sparkles, testid: 'help-menu-whats-new',
      onClick: () => { window.dispatchEvent(new CustomEvent('bp:whats-new')); setOpen(false); },
    },
    {
      label: 'Contact support', icon: MessageCircle, testid: 'help-menu-support',
      to: '/app/profile',
    },
  ];

  return (
    <>
      {/* Menu popover */}
      {open && (
        <div
          className="fixed inset-0"
          style={{ zIndex: 60 }}
          onClick={() => setOpen(false)}
        >
          <div
            data-testid="help-menu"
            onClick={(e) => e.stopPropagation()}
            className="fixed rounded-2xl overflow-hidden animate-fade-up"
            style={{
              bottom: 92, right: 20,
              backgroundColor: '#FFFFFF',
              boxShadow: '0 20px 50px -12px rgba(11,25,44,0.35), 0 8px 20px -8px rgba(11,25,44,0.15)',
              width: 288, zIndex: 61,
              border: '1px solid rgba(11,25,44,0.06)',
            }}
          >
            <div className="px-4 pt-4 pb-2 border-b" style={{ borderColor: 'rgba(11,25,44,0.06)' }}>
              <p className="text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">Help</p>
              <p className="font-heading text-base text-navy mt-0.5 leading-snug">How can we help?</p>
            </div>
            <ul className="py-1">
              {items.map((it) => (
                <li key={it.label}>
                  {it.to ? (
                    <Link
                      to={it.to} data-testid={it.testid} onClick={() => setOpen(false)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy hover:bg-beige transition-smooth"
                    >
                      <it.icon size={15} className="text-navy/60" strokeWidth={1.75}/>{it.label}
                    </Link>
                  ) : (
                    <button
                      data-testid={it.testid} onClick={it.onClick}
                      className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-navy hover:bg-beige transition-smooth"
                    >
                      <it.icon size={15} className="text-navy/60" strokeWidth={1.75}/>{it.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Floating trigger — bottom of the FAB stack. Clears the Emergent preview badge with safe-area padding. */}
      <FabTooltip label={open ? 'Close menu' : 'Help & shortcuts'}>
      <button
        data-testid="help-button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Help"
        className="fixed w-12 h-12 rounded-full flex items-center justify-center text-white transition-smooth hover:-translate-y-0.5"
        style={{
          right: '1.5rem',
          bottom: 'calc(6.5rem + env(safe-area-inset-bottom, 0px))',
          backgroundColor: '#0B192C',
          boxShadow: '0 12px 30px -6px rgba(11,25,44,0.55)',
          zIndex: 62,
        }}
      >
        {open ? <X size={18}/> : <HelpCircle size={18} strokeWidth={1.75}/>}
      </button>
      </FabTooltip>
    </>
  );
}

// (menu items defined inline above)
